<script setup>
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useThemeColors } from 'dashboard/composables/useThemeColors';
import BarChart from 'shared/components/charts/BarChart.vue';

// eslint-disable-next-line no-unused-vars
const store = useStore();
const teamSummaryData = useMapGetter('summaryReports/getTeamSummaryReports');
const { getChartColors } = useThemeColors();

const chartData = computed(() => {
  const dataToUse = teamSummaryData.value;

  if (!dataToUse || dataToUse.length === 0) {
    return { labels: [], datasets: [] };
  }

  const totalConversations = dataToUse.reduce((sum, team) => {
    return sum + (team.conversationsCount || 0);
  }, 0);

  const totalMessages = dataToUse.reduce((sum, team) => {
    return sum + (team.totalMessagesCount || 0);
  }, 0);

  if (totalConversations === 0 && totalMessages === 0) {
    return { labels: [], datasets: [] };
  }

  const teamsWithData = dataToUse
    .filter(
      team =>
        (team.conversationsCount || 0) > 0 || (team.totalMessagesCount || 0) > 0
    )
    .map(team => ({
      name: team.name || 'Equipe sem nome',
      conversationPercentage:
        totalConversations > 0
          ? Math.round(
              ((team.conversationsCount || 0) / totalConversations) * 100
            )
          : 0,
      messagePercentage:
        totalMessages > 0
          ? Math.round(((team.totalMessagesCount || 0) / totalMessages) * 100)
          : 0,
      totalScore:
        (team.conversationsCount || 0) + (team.totalMessagesCount || 0),
    }))
    .sort((a, b) => b.totalScore - a.totalScore);

  const chartColors = getChartColors();

  return {
    labels: teamsWithData.map(team => team.name),
    datasets: [
      {
        label: 'Atendimentos',
        data: teamsWithData.map(team => team.conversationPercentage),
        backgroundColor: chartColors.chartPalette[0] || '#0062B1',
        borderColor: chartColors.chartPalette[0] || '#0062B1',
        borderWidth: 1,
      },
      {
        label: 'Mensagens',
        data: teamsWithData.map(team => team.messagePercentage),
        backgroundColor: chartColors.chartPalette[1] || '#1C7CFC',
        borderColor: chartColors.chartPalette[1] || '#1C7CFC',
        borderWidth: 1,
      },
    ],
  };
});

const chartOptions = computed(() => {
  const chartColors = getChartColors();

  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          color: chartColors.textPrimary,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: chartColors.tooltipBg,
        titleColor: chartColors.textPrimary,
        bodyColor: chartColors.textSecondary,
        borderColor: chartColors.tooltipBorder,
        borderWidth: 1,
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            const value = context.parsed.x || 0;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          // eslint-disable-next-line func-names
          callback: function (value) {
            return value + '%';
          },
          color: chartColors.textSecondary,
        },
        grid: {
          color: chartColors.gridColor,
        },
      },
      y: {
        ticks: {
          color: chartColors.textSecondary,
        },
        grid: {
          display: false,
        },
      },
    },
  };
});

const hasData = computed(() => {
  return chartData.value.labels.length > 0;
});
</script>

<template>
  <div
    class="bg-n-solid-2 rounded-xl p-4 shadow outline-1 outline outline-n-container w-full max-w-full"
  >
    <h3 class="text-lg font-medium text-n-slate-12 mb-4 text-center">
      {{ $t('REPORT.TEAM_PERFORMANCE_CHART_TITLE') }}
    </h3>
    <div v-if="hasData" class="h-80 w-full">
      <BarChart :collection="chartData" :chart-options="chartOptions" />
    </div>
    <div v-else class="flex items-center justify-center h-80">
      <span class="text-sm text-slate-600">
        {{ $t('REPORT.NO_ENOUGH_DATA') }}
      </span>
    </div>
  </div>
</template>
